import { useState, useEffect } from "react";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { EnvelopeSimple, PencilSimple, ArrowCounterClockwise, Eye, Code, FloppyDisk } from "@phosphor-icons/react";

export default function EmailTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [variables, setVariables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editTemplate, setEditTemplate] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchTemplates = () => {
    API.get("/tenant/email-templates").then(({ data }) => setTemplates(data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchTemplates();
    API.get("/tenant/email-template-variables").then(({ data }) => setVariables(data)).catch(() => {});
  }, []);

  const handleSave = async () => {
    if (!editTemplate) return;
    setSaving(true);
    try {
      await API.put(`/tenant/email-templates/${editTemplate.type}`, {
        subject: editTemplate.subject,
        body: editTemplate.body,
      });
      toast.success("Template saved");
      setEditTemplate(null);
      fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async (type) => {
    try {
      await API.delete(`/tenant/email-templates/${type}`);
      toast.success("Template reset to default");
      fetchTemplates();
    } catch {
      toast.error("Failed to reset");
    }
  };

  const renderPreview = (body) => {
    return body
      .replace(/\{\{(\w+)\}\}/g, '<span style="background:#dbeafe;padding:1px 4px;border-radius:3px;font-size:11px;font-family:monospace;">$1</span>');
  };

  if (loading) return <div className="text-center py-20 text-muted-foreground">Loading templates...</div>;

  return (
    <div data-testid="email-templates-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-teal-600 to-cyan-600 flex items-center justify-center">
          <EnvelopeSimple size={18} className="text-white" weight="fill" />
        </div>
        <div>
          <h1 className="text-xl sm:text-2xl font-bold font-[Chivo] tracking-tight">Email Templates</h1>
          <p className="text-xs text-muted-foreground">Customise the emails sent from your workspace</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="templates-grid">
        {templates.map(t => (
          <div key={t.type} className="border border-border rounded-lg p-4 bg-card" data-testid={`template-${t.type}`}>
            <div className="flex items-start justify-between mb-2">
              <div>
                <h3 className="text-sm font-bold font-[Chivo]">{t.name}</h3>
                <p className="text-[10px] text-muted-foreground font-mono">{t.type}</p>
              </div>
              {t.is_custom ? (
                <Badge className="text-[8px] bg-blue-100 text-blue-700">Custom</Badge>
              ) : (
                <Badge variant="outline" className="text-[8px]">Default</Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground mb-3 truncate">{t.subject}</p>
            <div className="flex flex-wrap gap-1 mb-3">
              {t.variables?.slice(0, 4).map(v => (
                <span key={v} className="text-[8px] px-1.5 py-0.5 rounded bg-muted font-mono text-muted-foreground">{`{{${v}}}`}</span>
              ))}
              {(t.variables?.length || 0) > 4 && <span className="text-[8px] text-muted-foreground">+{t.variables.length - 4}</span>}
            </div>
            <div className="flex gap-1.5">
              <Button variant="outline" size="sm" className="text-xs flex-1" onClick={() => setEditTemplate({ ...t })} data-testid={`edit-template-${t.type}`}>
                <PencilSimple size={12} className="mr-1" /> Edit
              </Button>
              <Button variant="ghost" size="sm" className="text-xs" onClick={() => setPreviewTemplate(t)} data-testid={`preview-template-${t.type}`}>
                <Eye size={12} />
              </Button>
              {t.is_custom && (
                <Button variant="ghost" size="sm" className="text-xs" onClick={() => handleReset(t.type)} data-testid={`reset-template-${t.type}`}>
                  <ArrowCounterClockwise size={12} />
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Edit Dialog */}
      <Dialog open={!!editTemplate} onOpenChange={open => !open && setEditTemplate(null)}>
        <DialogContent className="max-w-2xl" data-testid="edit-template-dialog">
          <DialogHeader><DialogTitle>Edit Template: {editTemplate?.name}</DialogTitle></DialogHeader>
          {editTemplate && (
            <div className="space-y-4">
              <div>
                <Label>Subject Line</Label>
                <Input
                  value={editTemplate.subject}
                  onChange={e => setEditTemplate({ ...editTemplate, subject: e.target.value })}
                  className="mt-1 font-mono text-xs"
                  data-testid="template-subject-input"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label>Email Body (HTML)</Label>
                  <div className="flex items-center gap-1">
                    <Code size={12} className="text-muted-foreground" />
                    <span className="text-[9px] text-muted-foreground">HTML supported</span>
                  </div>
                </div>
                <textarea
                  value={editTemplate.body}
                  onChange={e => setEditTemplate({ ...editTemplate, body: e.target.value })}
                  className="w-full h-48 rounded-md border border-border bg-background px-3 py-2 text-xs font-mono resize-y focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  data-testid="template-body-input"
                />
              </div>
              <div>
                <p className="text-[9px] text-muted-foreground mb-1 font-semibold">Available Variables:</p>
                <div className="flex flex-wrap gap-1">
                  {editTemplate.variables?.map(v => (
                    <button key={v} type="button" onClick={() => setEditTemplate({ ...editTemplate, body: editTemplate.body + `{{${v}}}` })}
                      className="text-[9px] px-1.5 py-0.5 rounded bg-blue-50 dark:bg-blue-950/30 text-blue-600 font-mono hover:bg-blue-100 cursor-pointer"
                    >
                      {`{{${v}}}`}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setEditTemplate(null)}>Cancel</Button>
                <Button className="flex-1" onClick={handleSave} disabled={saving} data-testid="save-template-btn">
                  <FloppyDisk size={14} className="mr-1" /> {saving ? "Saving..." : "Save Template"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={!!previewTemplate} onOpenChange={open => !open && setPreviewTemplate(null)}>
        <DialogContent className="max-w-lg" data-testid="preview-template-dialog">
          <DialogHeader><DialogTitle>Preview: {previewTemplate?.name}</DialogTitle></DialogHeader>
          {previewTemplate && (
            <div>
              <div className="border border-border rounded-md p-3 mb-3 bg-muted/20">
                <p className="text-[9px] text-muted-foreground uppercase tracking-wider mb-1">Subject</p>
                <p className="text-sm font-medium" dangerouslySetInnerHTML={{ __html: renderPreview(previewTemplate.subject) }} />
              </div>
              <div className="border border-border rounded-md p-4 bg-white dark:bg-gray-950">
                <div className="text-sm" dangerouslySetInnerHTML={{ __html: renderPreview(previewTemplate.body) }} />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
